import { HospitalListingOutput } from '@/helpers/parseHospitalListings';
import { useEffect, useState, useTransition } from 'react';
import { MatchInfoProps, SelectInfoProps } from '../HospitalMultiRequestFooter';
import { RequestStatusEnum } from '@/types/types';
import { useRouter } from 'next/navigation';
import { ibow2_proto_PortalPatient } from '@/init/swagger';

type Props = {
  allHospitalListings: HospitalListingOutput[][];
  portalPatient: ibow2_proto_PortalPatient | undefined;
};

export const useMultiRequest = ({
  allHospitalListings,
  portalPatient,
}: Props) => {
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const [multiRequestListings, setMultiRequestListings] = useState<
    MultiRequestListing[][]
  >(buildInitialMultiRequestListings(allHospitalListings));

  const [excludeRequested, setExcludeRequested] = useState(false);
  const handleToggleExcludeRequested = (checked: boolean) => {
    setExcludeRequested(checked);
  };

  // 一括送信モード専用のページネーション情報
  const [multiRequestPage, setMultiRequestPage] = useState(1);
  const multiRequestPaginationInfo = {
    lastPage: multiRequestListings.length,
    current: {
      page: multiRequestPage,
    },
  };

  // 掲載情報を選択したときのチェック状態の切り替え処理
  const handleSelectListing = (selectedListing: HospitalListingOutput) => {
    setMultiRequestListings((prev) => {
      return prev.map((hospitalListings) => {
        return hospitalListings.map((hospitalListing) => {
          if (hospitalListing.listing.id === selectedListing.id) {
            return {
              ...hospitalListing,
              checked: !hospitalListing.checked,
            };
          }
          return hospitalListing;
        });
      });
    });
  };

  const toggleAllSelection = () => {
    setMultiRequestListings((prev) => {
      return prev.map((hospitalListings) => {
        return hospitalListings.map((hospitalListing) => {
          if (hospitalListing.disabled) {
            return hospitalListing;
          }

          return {
            ...hospitalListing,
            checked: !multiRequestListings
              .flat()
              .filter(({ disabled }) => !disabled)
              .every(({ checked }) => checked),
          };
        });
      });
    });
  };

  // NOTE: 紹介可能の表示情報
  const matchInfo: MatchInfoProps = {
    key: 'match',
    // disabled: false を持つものの数をカウントする
    value: multiRequestListings.reduce((acc, hospitalListings) => {
      return acc + hospitalListings.filter(({ disabled }) => !disabled).length;
    }, 0),
    excludeRequested,
    onToggleExcludeRequested: handleToggleExcludeRequested,
  };

  // NOTE: 選択の表示情報
  const selectInfo: SelectInfoProps = {
    key: 'select',
    // checked: true を持つものの数をカウントする
    value: multiRequestListings.reduce((acc, hospitalListings) => {
      return acc + hospitalListings.filter(({ checked }) => checked).length;
    }, 0),
    excludeRequested,
    onToggleExcludeRequested: handleToggleExcludeRequested,
    // 一括送信画面へ遷移
    onMultiRequest: () => {
      const newQuery = new URLSearchParams();

      /**
       * リクエストが長くなってしまうので,で区切る
       */
      const portalOfficeIds = multiRequestListings
        .flat()
        .filter(({ checked }) => checked)
        .map(({ listing }) => listing.portal_office_id)
        .join(',');

      newQuery.append('portalOfficeIds', portalOfficeIds);

      newQuery.append('target', 'hospital');
      if (portalPatient?.id) {
        newQuery.append('patient_id', portalPatient.id.toString());
      }

      startTransition(() => {
        router.push(`/app/requests/create-multi?${newQuery.toString()}`);
      });
    },
  };

  // NOTE: サーバーコンポーネントから渡ってくるデータが更新されたときに初期化する処理
  useEffect(() => {
    setMultiRequestListings(
      buildInitialMultiRequestListings(allHospitalListings),
    );
    setMultiRequestPage(1);
    setExcludeRequested(false);
  }, [allHospitalListings]);

  // NOTE: 照会中を除外するかどうかの切り替え処理
  useEffect(() => {
    setMultiRequestListings((prev) => {
      return prev.map((hospitalListings) => {
        return hospitalListings.map(({ listing, checked, disabled }) => {
          // 紹介中でないならそのまま返す
          if (listing.request_status !== RequestStatusEnum.Introduction) {
            return {
              listing,
              checked,
              disabled,
            };
          }

          // 紹介中の場合、除外するかどうかで分岐
          // 除外しない場合は、disabledをfalseにしてcheckedをtrueにする
          if (excludeRequested) {
            // 除外する場合は、チェックを外して非活性にする
            return {
              listing,
              checked: false,
              disabled: true,
            };
          } else {
            // 除外しない場合は、チェックはそのままで、活性化する
            return {
              listing,
              checked,
              disabled: false,
            };
          }
        });
      });
    });
  }, [excludeRequested]);

  return {
    multiRequestListings,
    multiRequestPage,
    setMultiRequestPage,
    handleSelectListing,
    matchInfo,
    selectInfo,
    multiRequestPaginationInfo,
    toggleAllSelection,
    isPending,
  };
};

type MultiRequestListing = {
  listing: HospitalListingOutput;
  checked: boolean;
  disabled: boolean;
};

// サーバーコンポーネントから渡ってくるデータを元に、
// チェックボックスの状態を初期化する
const buildInitialMultiRequestListings = (
  allHospitalListings: HospitalListingOutput[][],
): MultiRequestListing[][] => {
  return allHospitalListings.map((hospitalListings) => {
    return hospitalListings.map((listing) => {
      // けあログっとで作成されていないものは選択できない
      if (!listing.portal_office.is_carelogood) {
        return {
          listing,
          checked: false,
          disabled: true,
        };
      }

      // 初期状態は全てチェックが入っている
      return {
        listing,
        checked: true,
        disabled: false,
      };
    });
  });
};
